import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { z } from 'zod'

import { apiScoping } from '@/lib/api/scoping'
import { hubspotSearchKeys } from '@/features/assessments/api/query-keys'

export const HUBSPOT_SEARCH_MIN_QUERY_LENGTH = 2
const HUBSPOT_SEARCH_LIMIT = 20

const HubspotSearchOwnerSchema = z.object({
  userId: z.string().nullable(),
  name: z.string(),
  imageUrl: z.string().nullable(),
  email: z.string().nullable()
})

export type HubspotSearchOwner = z.infer<typeof HubspotSearchOwnerSchema>

export const HubspotSearchItemSchema = z.object({
  id: z.string(),
  type: z.enum(['deal', 'plant']),
  hubspotId: z.string(),
  title: z.string(),
  address: z.string().nullable(),
  lastActivityAt: z.string().nullable(),
  stageId: z.string().nullable(),
  plantHubspotId: z.string().nullable(),
  plantTitle: z.string().nullable(),
  suggestedCountry: z.string().nullable(),
  dealOwner: HubspotSearchOwnerSchema.nullable(),
  salesEngineerOwner: HubspotSearchOwnerSchema.nullable()
})

export type HubspotSearchItem = z.infer<typeof HubspotSearchItemSchema>

export type HubspotSearchItemType = HubspotSearchItem['type']

const HubspotSearchResponseSchema = z.object({
  items: z.array(HubspotSearchItemSchema)
})

export const HubspotDuplicateSchema = z.object({
  plantAssessmentId: z.string(),
  plantName: z.string(),
  matchedBy: z.enum(['deal', 'plant'])
})

export type HubspotDuplicate = z.infer<typeof HubspotDuplicateSchema>

const HubspotDuplicatesResponseSchema = z.object({
  items: z.array(HubspotDuplicateSchema)
})

export type HubspotDuplicatesParams = {
  dealId?: string | null
  plantId?: string | null
}

// A 4xx (short query, flag off) will not change on retry.
function retryUnlessClientError(failureCount: number, error: unknown): boolean {
  const status = isAxiosError(error) ? error.response?.status : undefined
  if (status !== undefined && status >= 400 && status < 500) return false
  return failureCount < 3
}

export async function fetchHubspotSearch(
  q: string,
  type?: HubspotSearchItemType
): Promise<HubspotSearchItem[]> {
  const res = await apiScoping.get('/api/v1/hubspot-search', {
    params: { q, limit: HUBSPOT_SEARCH_LIMIT, ...(type ? { type } : {}) }
  })
  return HubspotSearchResponseSchema.parse(res.data).items
}

// Null when the plant is not materialized (404), so the field can fall back to the bare ID.
export async function fetchHubspotPlant(hubspotId: string): Promise<HubspotSearchItem | null> {
  try {
    const res = await apiScoping.get(
      `/api/v1/hubspot-search/plants/${encodeURIComponent(hubspotId)}`
    )
    return HubspotSearchItemSchema.parse(res.data)
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) return null
    throw error
  }
}

// Null when the deal is not materialized (404), so the field can fall back to the bare ID.
export async function fetchHubspotDeal(hubspotId: string): Promise<HubspotSearchItem | null> {
  try {
    const res = await apiScoping.get(
      `/api/v1/hubspot-search/deals/${encodeURIComponent(hubspotId)}`
    )
    return HubspotSearchItemSchema.parse(res.data)
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) return null
    throw error
  }
}

export async function fetchHubspotDuplicates(
  params: HubspotDuplicatesParams
): Promise<HubspotDuplicate[]> {
  const res = await apiScoping.get('/api/v1/hubspot-search/duplicates', {
    params: {
      ...(params.dealId ? { dealId: params.dealId } : {}),
      ...(params.plantId ? { plantId: params.plantId } : {})
    }
  })
  return HubspotDuplicatesResponseSchema.parse(res.data).items
}

// The caller debounces `q`; this hook only trims it and gates on its length.
export function hubspotSearchQueryOptions(q: string, type?: HubspotSearchItemType) {
  const trimmed = q.trim()
  return queryOptions({
    queryKey: hubspotSearchKeys.search(trimmed, type),
    queryFn: () => fetchHubspotSearch(trimmed, type),
    enabled: trimmed.length >= HUBSPOT_SEARCH_MIN_QUERY_LENGTH,
    placeholderData: keepPreviousData,
    retry: retryUnlessClientError,
    staleTime: 60_000
  })
}

export function useHubspotSearch(q: string, type?: HubspotSearchItemType) {
  return useQuery(hubspotSearchQueryOptions(q, type))
}

export function hubspotPlantQueryOptions(hubspotId: string | null) {
  return queryOptions({
    queryKey: hubspotSearchKeys.plant(hubspotId ?? ''),
    queryFn: () => fetchHubspotPlant(hubspotId ?? ''),
    enabled: !!hubspotId,
    retry: retryUnlessClientError,
    staleTime: 5 * 60_000
  })
}

export function useHubspotPlant(hubspotId: string | null) {
  return useQuery(hubspotPlantQueryOptions(hubspotId))
}

export function hubspotDealQueryOptions(hubspotId: string | null) {
  return queryOptions({
    queryKey: hubspotSearchKeys.deal(hubspotId ?? ''),
    queryFn: () => fetchHubspotDeal(hubspotId ?? ''),
    enabled: !!hubspotId,
    retry: retryUnlessClientError,
    staleTime: 5 * 60_000
  })
}

export function useHubspotDeal(hubspotId: string | null) {
  return useQuery(hubspotDealQueryOptions(hubspotId))
}

export function hubspotDuplicatesQueryOptions(params: HubspotDuplicatesParams) {
  const dealId = params.dealId || null
  const plantId = params.plantId || null
  return queryOptions({
    queryKey: hubspotSearchKeys.duplicates({ dealId, plantId }),
    queryFn: () => fetchHubspotDuplicates({ dealId, plantId }),
    enabled: dealId !== null || plantId !== null,
    retry: retryUnlessClientError,
    staleTime: 60_000
  })
}

export function useHubspotDuplicates(params: HubspotDuplicatesParams) {
  return useQuery(hubspotDuplicatesQueryOptions(params))
}
